import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export type Json = string | number | boolean | null | { [k: string]: Json } | Json[];

export type PageSeo = {
  id?: string;
  path: string;
  title: string | null;
  description: string | null;
  keywords: string | null;
  og_image: string | null;
  og_type: string | null;
  canonical: string | null;
  robots: string | null;
  json_ld: Json | null;
  active: boolean;
  updated_at?: string;
};

const SELECT_COLS =
  "id,path,title,description,keywords,og_image,og_type,canonical,robots,json_ld,active,updated_at";

// PUBLIC — used by route loaders to override default head()
export const getPageSeo = createServerFn({ method: "GET" })
  .inputValidator((input: { path: string }) => ({
    path: String(input?.path ?? "").trim().toLowerCase() || "/",
  }))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: row, error } = await supabaseAdmin
      .from("page_seo")
      .select(SELECT_COLS)
      .eq("path", data.path)
      .eq("active", true)
      .maybeSingle();
    if (error) {
      console.error("getPageSeo:", error);
      return null;
    }
    return (row as PageSeo | null) ?? null;
  });

// ADMIN
export const adminListPageSeo = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("page_seo")
      .select(SELECT_COLS)
      .order("path", { ascending: true });
    if (error) throw new Error(error.message);
    return (data ?? []) as PageSeo[];
  });

export const adminUpsertPageSeo = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: Partial<PageSeo> & { path: string }) => {
    const path = String(input.path ?? "").trim().toLowerCase();
    if (!path.startsWith("/")) throw new Error("Path must start with /");
    let jsonLd: Json | null = null;
    if (input.json_ld != null && input.json_ld !== "") {
      if (typeof input.json_ld === "string") {
        try { jsonLd = JSON.parse(input.json_ld) as Json; }
        catch { throw new Error("JSON-LD must be valid JSON"); }
      } else {
        jsonLd = input.json_ld as Json;
      }
    }
    return {
      path,
      title: input.title?.toString().trim() || null,
      description: input.description?.toString().trim() || null,
      keywords: input.keywords?.toString().trim() || null,
      og_image: input.og_image?.toString().trim() || null,
      og_type: input.og_type?.toString().trim() || null,
      canonical: input.canonical?.toString().trim() || null,
      robots: input.robots?.toString().trim() || null,
      json_ld: jsonLd,
      active: input.active !== false,
    };
  })
  .handler(async ({ data, context }) => {
    const { data: row, error } = await context.supabase
      .from("page_seo")
      .upsert(data, { onConflict: "path" })
      .select(SELECT_COLS)
      .single();
    if (error) throw new Error(error.message);
    return row as PageSeo;
  });

export const adminDeletePageSeo = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { id: string }) => ({ id: String(input.id) }))
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase.from("page_seo").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });
